import { AD_DETAILS_API_IDS, PIXR_CLIENT_KEY, changeDescriptionForMultiline } from '~/helpers/adDetails';
import { MAX_ELASTICSEARCH_RESULTS } from '~/helpers/catalogue';
import { appendParamsToEndpoint } from '~/helpers/general';
import { api } from '~/services/api';
import {
  AD_SAVE_SUCCESS,
  ERROR_FORM_SCHEMA,
  IMAGE_REPLACE_ERROR,
  IMAGE_REPLACE_LOADING,
  IMAGE_REPLACE_SUCCESS,
  IMAGE_UPLOAD,
  IMAGE_UPLOAD_ERROR,
  IMAGE_UPLOAD_SUCCESS,
  LOADED_FORM_SCHEMA,
  LOAD_FORM_SCHEMA,
  LOAD_PRODUCTS,
  LOAD_PRODUCTS_ERROR,
  LOAD_PRODUCTS_SUCCESS,
} from '~/store/reducers/products';

export function loadProducts(params = {}, paramsDrawer) {
  let params_drawer = paramsDrawer === undefined ? null : paramsDrawer;
  return (dispatch, getState) => {
    let {
      products: { product_list, next_page_tokens },
      session: { active_merchant },
    } = getState();
    let currentPage = product_list[active_merchant.id] ? product_list[active_merchant.id].currentPage : 1;

    dispatch({ type: LOAD_PRODUCTS });
    if (!params.page && typeof params === 'object') {
      params.page = currentPage;
    }
    let url;

    const baseUrl = `/${active_merchant.id}/products/`;
    let requestParams = typeof params === 'string' ? null : { ...params };

    // Handle deep pagination with search_after
    if (requestParams && requestParams.page * requestParams.limit > MAX_ELASTICSEARCH_RESULTS) {
      // Use search_after token for pages beyond 11000 results
      const searchKey = `${active_merchant.id}_${requestParams.page - 1}`;
      const token = next_page_tokens[searchKey];

      if (token) {
        requestParams.search_after = JSON.stringify(token);
        delete requestParams.page; // Don't send page param with search_after
      }
    }

    // NOTE: params peut être une string si l'utilisateur a navigué
    // dans /annonces en mettant dans l'url les paramètres de recherche
    if (typeof params === 'string') {
      url = baseUrl + params;
    } else {
      url = appendParamsToEndpoint(baseUrl, requestParams);
    }
    url = url.replace('status=&', '');
    api
      .get(url)
      .then((response) => {
        if (response.success === true) {
          return dispatch({
            type: LOAD_PRODUCTS_SUCCESS,
            payload: {
              merchant_id: active_merchant.id,
              products_count: response.data.total,
              product_list: response.data.results,
              requested_page: params.page || 1,
              next_page_token: response.data.search_after || null,
              has_next: response.data.has_next || false,
              params_drawer: params_drawer,
            },
          });
        }
        return dispatch({
          type: LOAD_PRODUCTS_ERROR,
          error: response.message.error,
        });
      })
      .catch((error) => dispatch({ type: LOAD_PRODUCTS_ERROR, error }));
  };
}

export function updateAd({ productId, modifiedFields, publish, photoroomDisabled = false }, callback, errorCallback) {
  return (dispatch, getState) => {
    const {
      session: { active_merchant },
    } = getState();

    const formFieldsToSend = { ...modifiedFields };

    if (formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION]) {
      formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION] = changeDescriptionForMultiline(
        formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION]
      );
    }

    const body = JSON.stringify({
      components: [formFieldsToSend],
      merchant: active_merchant.id,
    });

    api
      .patch(`/product/${productId}/`, { body })
      .then((response) => {
        if (response.success) {
          if (publish) {
            return api.patch(`/product/${response.data.product_id}/activate/?skip_photoroom=true`);
          }
          dispatch({ type: AD_SAVE_SUCCESS });
          callback(response.data.product_id);
        } else {
          errorCallback(response.message.error);
        }
      })
      .then((response) => {
        // NOTE: if you don't return anything from a then-function, you are effectively returning a resolved promise for the value undefined
        // so if response is undefined we know that we didn't return the api.patch
        // so no need to execute the callback twice
        if (response == null) {
          return;
        }
        if (response.success) {
          if (!photoroomDisabled) {
            api.put(`/product/${response.data.product_id}/images/photoroom/`).catch(() => {});
          }

          dispatch({ type: AD_SAVE_SUCCESS });
          callback(response.data.product_id);
        } else {
          errorCallback(response.message.error);
        }
      })
      .catch((error) => {
        errorCallback(error);
      });
  };
}

// Créé un brouillon et l'active si besoin
export function createAd(publish = false, callback, errorCallback, photoroomDisabled = false) {
  return (dispatch, getState) => {
    const {
      products: { form_fields, last_shown_suggested_price },
      session,
    } = getState();
    const { active_merchant } = session;

    const formFieldsToSend = { ...form_fields };

    if (formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION]) {
      formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION] = changeDescriptionForMultiline(
        formFieldsToSend[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION]
      );
    }

    const formDataToSend = {
      components: [formFieldsToSend],
      merchant: active_merchant.id,
    };

    // Internal analytics: echo back the Enhancy suggested price the vendor saw
    // (if any) so the backend can measure acceptance. Omit the field entirely
    // when no suggestion was shown — never send null or 0.
    if (last_shown_suggested_price != null) {
      formDataToSend.suggested_price = last_shown_suggested_price;
    }

    api
      .post(`/${active_merchant.id}/product/create/`, {
        body: JSON.stringify(formDataToSend),
      })
      .then((response) => {
        if (response.success) {
          if (publish === true) {
            return api.patch(`/product/${response.data.product_id}/activate/?skip_photoroom=true`);
          }
          dispatch({ type: AD_SAVE_SUCCESS });
          return callback(response.data.product_id);
        }
        errorCallback(response.message.error);
      })
      .then((response) => {
        // NOTE: if you don't return anything from a then-function, you are effectively returning a resolved promise for the value undefined
        // so if response is undefined we know that we didn't return the api.patch
        // so no need to execute the callback twice
        if (response == null) {
          return;
        }
        if (response.success) {
          if (!photoroomDisabled) {
            api.put(`/product/${response.data.product_id}/images/photoroom/`).catch(() => {});
          }

          dispatch({ type: AD_SAVE_SUCCESS });
          return callback(response.data.product_id);
        }
        return errorCallback(response.message.error);
      });
  };
}

export function uploadImage(file, callBackError) {
  return (dispatch) => {
    let random_id = Math.random().toString().slice(2, 10);

    dispatch({ type: IMAGE_UPLOAD, random_id });

    let form_data = new FormData();

    form_data.append('file', file);

    api
      .post(`/upload_image/?PIXR_CLIENT_KEY=${PIXR_CLIENT_KEY}&path=labo&overwrite=1`, {
        body: form_data,
      })
      .then((response) => {
        if (response.success) {
          dispatch({
            type: IMAGE_UPLOAD_SUCCESS,
            url: response.url,
            image_id: response.image_id,
            random_id,
          });
        } else {
          dispatch({
            type: IMAGE_UPLOAD_ERROR,
            error: response.message.error,
            random_id,
          });
          callBackError(file, response.message.error);
        }
      })
      .catch((error) => {
        dispatch({ type: IMAGE_UPLOAD_ERROR, error, random_id });
        callBackError(file, error);
      });
  };
}

export function replaceImage(file, order, callbackSuccess, callbackError) {
  return (dispatch) => {
    dispatch({
      type: IMAGE_REPLACE_LOADING,
      order: order,
    });
    let random_id = Math.random().toString().slice(2, 10);

    dispatch({ type: IMAGE_UPLOAD, random_id });

    let form_data = new FormData();

    form_data.append('file', file);

    api
      .post(`/upload_image/?PIXR_CLIENT_KEY=${PIXR_CLIENT_KEY}&path=labo`, {
        body: form_data,
      })
      .then((response) => {
        if (response.success) {
          dispatch({
            type: IMAGE_REPLACE_SUCCESS,
            url: response.url,
            image_id: response.image_id,
            order,
          });
          callbackSuccess();
        } else {
          dispatch({
            type: IMAGE_REPLACE_ERROR,
            error: response.message.error,
            random_id,
          });
          callbackError(response.message.error);
        }
      })
      .catch((error) => {
        dispatch({ type: IMAGE_UPLOAD_ERROR, error, random_id });
        callbackError(error);
      });
  };
}

// Recupere les choix de categorie et Label affaires pour l'edition de masse, les propositions
// des champs et les textes d'aides sont geres dynamiquement
export function loadFormSchemaThunk(product_id, with_sku = true) {
  return (dispatch, getState) => {
    let {
      session: { active_merchant },
    } = getState();
    dispatch({ type: LOAD_FORM_SCHEMA });

    let url = product_id ? `/product/${product_id}/` : `/${active_merchant.id}/product/create/?without_sku=${!with_sku}`;

    api
      .get(url)
      .then((response) => {
        if (response.success) {
          dispatch({
            type: LOADED_FORM_SCHEMA,
            schema: response.data,
            with_sku: with_sku,
          });
        } else {
          dispatch({ type: ERROR_FORM_SCHEMA, error: response.info });
        }
      })
      .catch((error) => dispatch({ type: ERROR_FORM_SCHEMA, error }));
  };
}
